import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState } from "react";
import { Card, CardContent, CardHeader } from "../../../../../components/ui/common/card";
import { cn } from "../../../../../lib/utils";

interface CollapsibleSectionCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  badge?: React.ReactNode;
  /** Rendered in the header's right-hand slot, alongside the chevron. */
  headerRight?: React.ReactNode | ((open: boolean) => React.ReactNode);
  /** Rendered below the header row (e.g. an over-target warning). */
  headerBelow?: React.ReactNode;
  disabled?: boolean;
  defaultOpen?: boolean;
  headerClassName?: string;
  contentClassName?: string;
  children: React.ReactNode;
}

/**
 * Shared chrome for the process-detail sub-section cards (Waste, Chemical,
 * CIP/SIP, Recovery): a collapsible `Card` with an icon + title + optional
 * badge, an optional right-hand header slot, and an optional row below the
 * header. Content is `{open && …}`-gated rather than CSS-hidden, matching the
 * behaviour every section already had before this component existed.
 *
 * `disabled` only locks the content (inputs/buttons inert, dimmed) — the
 * header stays clickable so a locked section can still be expanded to view.
 */
const CollapsibleSectionCard: React.FC<CollapsibleSectionCardProps> = ({
  icon: Icon,
  title,
  badge,
  headerRight,
  headerBelow,
  disabled = false,
  defaultOpen = false,
  headerClassName,
  contentClassName,
  children
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card className="tw-border-border !tw-py-0">
      <CardHeader
        className={cn(
          "tw-bg-secondary/50 tw-py-2 tw-px-2 tw-rounded-t-lg tw-cursor-pointer",
          headerClassName
        )}
        onClick={() => setOpen(v => !v)}
      >
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Icon className="tw-h-4 tw-w-4 tw-text-primary" />
            <span className="tw-text-sm tw-font-semibold tw-text-foreground">{title}</span>
            {badge}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            {typeof headerRight === "function" ? headerRight(open) : headerRight}
            {open ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </div>
        {headerBelow}
      </CardHeader>
      {open && (
        <CardContent
          className={cn(
            "tw-py-2 tw-px-2 tw-space-y-2",
            disabled && "tw-opacity-50 tw-pointer-events-none",
            contentClassName
          )}
        >
          {children}
        </CardContent>
      )}
    </Card>
  );
};

export default CollapsibleSectionCard;
